// src/hooks/useToolCallProcessor.ts
/* eslint-disable @typescript-eslint/no-unused-vars */ // Mantener este comentario por si acaso marca los tipos
import { useEffect } from 'react';
// Eliminar la importación de ToolCall si no se usa como valor, SOLO como tipo
// O dejarla y confiar en el disable si la regla es muy estricta con los tipos
import { ToolCall, ServiceRequestArgs, LatestToolCallRequest } from '../types/chatTypes'; // ToolCall se usa aquí como tipo
// ELIMINAR LA SIGUIENTE LÍNEA:
// import { printServiceRequest } from '../utils/printingUtils'; // <-- Eliminar esta línea

interface UseToolCallProcessorParams {
    latestToolCallRequest: LatestToolCallRequest | null;
    isLoading: boolean;
    sendToolResult: (toolCallId: string, resultMessage: string) => Promise<void>;
    clearToolRequest: () => void;
    // Si tuvieras más tools, podrías pasar un objeto de manejadores aquí
    // Por ahora, pasamos directamente la función para la herramienta específica
    handleServiceRequestPrint: (args: ServiceRequestArgs) => boolean; // Esta función es la que usas
}

/**
 * Custom hook to process received tool call requests.
 * Handles validation and execution of specific tool calls (like printing a service request).
 */
export function useToolCallProcessor({
    latestToolCallRequest,
    isLoading,
    sendToolResult,
    clearToolRequest,
    handleServiceRequestPrint, // Recibimos la función de acción
}: UseToolCallProcessorParams) {

    useEffect(() => {
        // Procesa el tool call solo si hay una solicitud pendiente y no estamos cargando otra cosa
        if (latestToolCallRequest && !isLoading) {
            const toolCallId = latestToolCallRequest.id;
            const name = latestToolCallRequest.function?.name;
            const argsStr = latestToolCallRequest.function?.arguments;

            let resultMessage = ""; // Mensaje que se enviará de vuelta a la IA como resultado de la tool
            let shouldClearRequest = true; // Bandera para saber si limpiar latestToolCallRequest al final

            console.log(`Processing tool call: ${name} with ID ${toolCallId}`);

            if (name === 'create_service_request') {
                let args: ServiceRequestArgs | null = null;
                let validationError: string | null = null;

                let missing: string[] = []; // Declarar la variable missing fuera del bloque try

                try {
                    // Intentar parsear los argumentos JSON de la IA
                    args = JSON.parse(argsStr || '{}') as ServiceRequestArgs;

                    // Validar los campos requeridos
                    const required = ['nombre_solicitante', 'nombre_servicio', 'ubicacion_proyecto', 'detalles_adicionales'];
                    missing = required.filter(field =>
                        !args?.[field as keyof ServiceRequestArgs]?.toString().trim() ||
                        (field === 'detalles_adicionales' && args?.detalles_adicionales && args.detalles_adicionales.length < 10)
                    );

                     // Validar al menos un contacto (email o teléfono)
                     if ((!args?.email_solicitante || !args.email_solicitante.includes('@')) && (!args?.telefono_solicitante || args.telefono_solicitante.length < 8)) {
                         missing.push("contacto (email o teléfono válido)");
                     }


                    if (missing.length > 0) {
                        validationError = `Faltan datos o son inválidos: ${missing.join(', ')}.`;
                        console.warn("Tool call validation failed:", validationError, args);
                    }

                } catch (e) {
                    // Error al parsear el JSON
                    console.error("Error parsing tool call arguments:", e, argsStr);
                    validationError = "Error procesando los argumentos de la solicitud de servicio (JSON inválido).";
                }

                // Si los argumentos son válidos y no hay errores de validación
                if (args && !validationError) {
                    console.log("Tool call arguments valid. Executing action...");
                    // Ejecutar la acción (imprimir en este caso)
                    const actionSuccess = handleServiceRequestPrint(args);

                    // Preparar el mensaje de resultado para la IA
                    resultMessage = actionSuccess
                        ? "La solicitud de servicio fue generada exitosamente. Se ha mostrado el PDF al usuario."
                        : "Error al generar o mostrar la solicitud de servicio en formato PDF.";

                    // Si la acción fue exitosa, podríamos limpiar la solicitud de tool call.
                    // Si falló, quizás queremos que la IA lo intente de nuevo o pida más datos,
                    // pero por ahora, siempre limpiamos después de intentar procesar.
                    shouldClearRequest = true;

                } else {
                     // Si hubo un error de validación o parseo
                    console.log("Tool call arguments invalid. Reporting error back to AI.");
                    const missingFields = missing || [];
                    resultMessage = `Error de validación al intentar procesar la solicitud de servicio: ${validationError || "Datos incompletos o inválidos."}. Por favor, pide al usuario la información correcta/faltante para ${missingFields.join(', ')}.`;
                    shouldClearRequest = true; // Limpiamos la solicitud ya que la IA debe pedir datos de nuevo
                }

            } else if (name) {
                // La herramienta solicitada no es reconocida por este procesador
                resultMessage = `Error: La herramienta '${name}' no es reconocida por el sistema.`;
                console.warn(`Received unrecognized tool call: ${name}`);
                 shouldClearRequest = true;

            } else {
                 // El tool call recibido tiene un formato inesperado
                resultMessage = `Error: Tool call inválido recibido del modelo.`;
                console.error("Received invalid tool call format:", latestToolCallRequest);
                 shouldClearRequest = true;
            }

            // Enviar el resultado (o el error de validación/procesamiento) de vuelta a la IA
            // Asegurarse de que haya un ID de tool call y un mensaje de resultado antes de enviar
            if (toolCallId && resultMessage) {
                console.log(`Sending tool result for ID ${toolCallId}: "${resultMessage}"`);
                sendToolResult(toolCallId, resultMessage); // Llama a la función pasada desde useChat
            } else {
                 console.error("Cannot send tool result: Missing toolCallId or resultMessage.", { toolCallId, resultMessage });
            }

            // Limpiar la solicitud de tool call pendiente *después* de intentar enviar el resultado
            // Esto previene re-procesar la misma solicitud.
            if (shouldClearRequest) {
                clearToolRequest(); // Llama a la función pasada desde useChat
            }
        }
    }, [latestToolCallRequest, isLoading, sendToolResult, clearToolRequest, handleServiceRequestPrint]); // Dependencias del useEffect

    // Este hook no necesita retornar nada, su propósito es ejecutar un efecto secundario.
}